<template>
  <div class="documents-documentation-tab">
    <div class="text-h6 q-mb-md">Attached Documents</div>

    <q-card flat bordered>
      <q-card-section>
        <div class="text-subtitle2 q-mb-md">Upload Documents</div>

        <q-file
          v-model="newDocument"
          label="Select document to upload"
          outlined
          dense
          accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
          class="q-mb-md"
        >
          <template #prepend>
            <q-icon name="attach_file" />
          </template>
          <template #append>
            <q-btn
              flat
              round
              dense
              icon="cloud_upload"
              :disable="!newDocument"
              @click="uploadDocument"
            />
          </template>
        </q-file>

        <q-list bordered separator class="rounded-borders q-mb-md">
          <q-item v-for="(doc, index) in modelValue.attached_documents" :key="index">
            <q-item-section avatar>
              <q-icon :name="getDocumentIcon(doc.type)" color="primary" />
            </q-item-section>
            <q-item-section>
              <q-item-label>{{ doc.name }}</q-item-label>
              <q-item-label caption>{{ doc.type }} - {{ formatFileSize(doc.size) }}</q-item-label>
              <q-item-label caption>Uploaded: {{ formatDate(doc.uploaded_at) }}</q-item-label>
            </q-item-section>
            <q-item-section side>
              <div class="q-gutter-xs">
                <q-btn
                  flat
                  round
                  dense
                  icon="download"
                  color="primary"
                  @click="downloadDocument(doc)"
                >
                  <q-tooltip>Download</q-tooltip>
                </q-btn>
                <q-btn
                  flat
                  round
                  dense
                  icon="delete"
                  color="negative"
                  @click="removeDocument(index)"
                >
                  <q-tooltip>Remove</q-tooltip>
                </q-btn>
              </div>
            </q-item-section>
          </q-item>
          <q-item v-if="!modelValue.attached_documents || modelValue.attached_documents.length === 0">
            <q-item-section>
              <q-item-label class="text-grey-6">No documents attached</q-item-label>
              <q-item-label caption>Upload manuals, specifications, drawings, etc.</q-item-label>
            </q-item-section>
          </q-item>
        </q-list>

        <div class="text-caption text-grey-7">
          Supported formats: PDF, Word, Excel, Images (PNG, JPG)
        </div>
      </q-card-section>
    </q-card>
  </div>
</template>

<script setup>
  import { ref } from 'vue';

  /**
   * Documents Documentation Tab Component
   *
   * Displays interface for managing attached documents
   */

  const props = defineProps({
    modelValue: {
      type: Object,
      required: true,
      default: () => ({
        attached_documents: [],
      }),
    },
  });

  const emit = defineEmits(['upload-document', 'download-document', 'remove-document']);

  const newDocument = ref(null);

  function uploadDocument() {
    if (!newDocument.value) {
      return;
    }

    const documentData = {
      name: newDocument.value.name,
      type: newDocument.value.type,
      size: newDocument.value.size,
      uploaded_at: new Date().toISOString(),
      file: newDocument.value,
    };

    emit('upload-document', documentData);
    newDocument.value = null;
  }

  function downloadDocument(doc) {
    emit('download-document', doc);
  }

  function removeDocument(index) {
    emit('remove-document', index);
  }

  function getDocumentIcon(type) {
    if (type?.includes('pdf')) {
      return 'picture_as_pdf';
    }
    if (type?.includes('word') || type?.includes('doc')) {
      return 'description';
    }
    if (type?.includes('excel') || type?.includes('sheet')) {
      return 'table_chart';
    }
    if (type?.includes('image') || type?.includes('png') || type?.includes('jpg')) {
      return 'image';
    }
    return 'insert_drive_file';
  }

  function formatFileSize(bytes) {
    if (!bytes) {
      return '0 Bytes';
    }
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  }

  function formatDate(dateString) {
    if (!dateString) {
      return '';
    }
    return new Date(dateString).toLocaleDateString();
  }
</script>

<style scoped>
.documents-documentation-tab {
  height: 100%;
}
</style>
